/**
 * useAIPrefill — request lifecycle for AI form prefill.
 *
 * const { requestDraft, cancel, status, error, result, elapsedSeconds, reset }
 *   = useAIPrefill('ptw');
 *
 * Generation can take 5–60s: exposes elapsedSeconds for staged loading copy
 * and a cancel() backed by AbortController. Errors are pre-mapped to
 * user-facing messages (503 disabled / 403 permission / 429 rate / timeout).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { requestPrefill } from '../services/assistantClient';

// Must exceed the server's worst case (ASSISTANT_PREFILL_TIMEOUT per attempt,
// up to two attempts on validation retry — slow local Ollama needs minutes;
// Bedrock answers in seconds and never gets near this).
const CLIENT_TIMEOUT_MS = 390_000;

function errorFor(err, timedOut) {
  if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
    if (timedOut) {
      return { status: 504, message: 'The draft took too long to generate. Try a shorter description.' };
    }
    return { status: 0, message: 'Cancelled', cancelled: true };
  }
  const status = err?.response?.status || 0;
  const detail = err?.response?.data?.detail;
  if (status === 503) return { status, message: 'The AI assistant is temporarily unavailable. Please try again later.' };
  if (status === 403) return { status, message: 'You do not have permission to use AI prefill for this module.' };
  if (status === 429) return { status, message: typeof detail === 'string' ? detail : 'Too many AI requests — please try again in a few minutes.' };
  if (status === 422) return { status, message: typeof detail === 'string' ? detail : 'The AI could not produce a valid draft. Try rephrasing your description.' };
  if (status === 504) return { status, message: 'The draft took too long to generate. Try a shorter description.' };
  if (status === 404) return { status, message: 'AI prefill is not enabled for this module.' };
  return { status, message: typeof detail === 'string' ? detail : 'Something went wrong generating the draft.' };
}

export default function useAIPrefill(module) {
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const timeoutRef = useRef(null);
  const timedOutRef = useRef(false);

  const clearTimers = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timerRef.current = null;
    timeoutRef.current = null;
  };

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    setStatus('idle');
    setError(null);
    setResult(null);
    setElapsedSeconds(0);
  }, []);

  const requestDraft = useCallback(async ({ description, documentFileIds = [] }) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus('loading');
    setError(null);
    setResult(null);
    setElapsedSeconds(0);
    const started = Date.now();
    timerRef.current = setInterval(
      () => setElapsedSeconds(Math.floor((Date.now() - started) / 1000)),
      1000
    );
    timedOutRef.current = false;
    timeoutRef.current = setTimeout(() => {
      timedOutRef.current = true; // distinguish from a user Cancel click
      controller.abort();
    }, CLIENT_TIMEOUT_MS);

    try {
      const data = await requestPrefill(
        module, { description, documentFileIds }, { signal: controller.signal }
      );
      clearTimers();
      setResult(data);
      setStatus('success');
      return data;
    } catch (err) {
      clearTimers();
      const mapped = errorFor(err, timedOutRef.current);
      if (mapped.cancelled) {
        setStatus('idle');
        return null;
      }
      setError(mapped);
      setStatus('error');
      return null;
    }
  }, [module]);

  useEffect(() => () => {
    abortRef.current?.abort();
    clearTimers();
  }, []);

  return { requestDraft, cancel, status, error, result, elapsedSeconds, reset };
}
